import { ColumnFiltersState, Row, SortingState } from "@tanstack/react-table";

import { SearchFilterState } from "@/components/spatialAnalysis/spatialAnalysisTable";
import { normalizeFilterValue } from "@/lib/utils";
import { DataSchemaType, DatasetDataType } from "@/types/dataset";

import { formatNumber } from "./number";

export type DataRowValue = string | number | boolean | null | undefined;

export type DetailRow = Record<string, DataRowValue>;
interface Schema {
  attribute_name: string;
  data_type: DataSchemaType;
}

export const formatDatasetRows = (
  data: DetailRow[],
  schema?: Schema[]
): DetailRow[] => {
  if (!schema) return data;

  return data.map((row) => {
    const formattedRow = { ...row };

    for (const { attribute_name, data_type } of schema) {
      const key = normalizeFilterValue(attribute_name);
      const rawValue = row[key];

      if (rawValue === null || rawValue === undefined) continue;

      const isNumeric =
        data_type === DataSchemaType.FLOAT || data_type === DataSchemaType.INT;

      let formatType: "decimal" | "integer" = "integer";
      if (data_type === DataSchemaType.FLOAT) {
        formatType = "decimal";
      }

      if (isNumeric) {
        formattedRow[key] = String(
          formatNumber(Number(rawValue), formatType)
        ).replace(/,/g, "");
      } else {
        formattedRow[key] = String(rawValue);
      }
    }

    return formattedRow;
  });
};

export const includesStringFilter = <T>(
  row: Row<T>,
  columnId: string,
  filterValue: string
): boolean => {
  const value = row.getValue(columnId);

  return String(value)
    .toLowerCase()
    .includes(String(filterValue).toLowerCase());
};

export const getTableState = (
  currentPage: number,
  pageSize: number,
  sorting: SortingState,
  columnFilters: ColumnFiltersState
) => ({
  pagination: {
    pageIndex: currentPage - 1,
    pageSize
  },
  sorting,
  columnFilters
});

export const generateColumnFilters = (
  filterState: SearchFilterState
): ColumnFiltersState => {
  const { query, query_type, query_column } = filterState;

  if (!query || !query_column || !query_type) return [];

  return query
    .map((value, i) => {
      const column = query_column[i];
      const type = query_type[i];

      if (!value || !column || !type) return null;

      const cleanValue =
        type === DatasetDataType.TYPE_INTEGER ||
        type === DatasetDataType.TYPE_FLOAT
          ? String(value).replace(/,/g, "")
          : value;

      return { id: column, value: cleanValue };
    })
    .filter((f): f is { id: string; value: string } => f !== null);
};
